import React, { useEffect, useMemo, useRef, useState } from 'react';
import NewsFeedItem from './NewsFeedItem';
import { getNewsFeedItems, NewsFeedItem as NewsFeedItemType } from '../../services/newsFeedService';
import './NewsFeedSection.css';

const NewsFeedSection: React.FC = () => {
  const [newsItems, setNewsItems] = useState<NewsFeedItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const filterRef = useRef<HTMLDivElement>(null);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    newsItems.forEach((item) => item.tags?.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [newsItems]);

  const filteredItems = useMemo(() => {
    if (selectedTags.size === 0) return newsItems;
    return newsItems.filter((item) =>
      item.tags?.some((tag) => selectedTags.has(tag))
    );
  }, [newsItems, selectedTags]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const introContent = (
    <div className="news-feed-intro">
      <div className="news-feed-intro-inner">
        <i className="fas fa-lightbulb news-feed-intro-bulb" aria-hidden="true"></i>
        <div className="news-feed-intro-body">
          <p className="news-feed-intro-text">
            Команда <span className="news-feed-intro-accent">datacircle</span> еженедельно публикует HR-аналитику в формате инфографики.
            Аналитики команды прорабатывают несколько десятков СМИ в России и за рубежом, чтобы выбрать наиболее достоверные и интересные данные из сферы HR
            и структурировать их в формате дашбордов.
          </p>
          <p className="news-feed-intro-text">
            Данные можно сохранить для дальнейшего использования <i className="fas fa-bookmark news-feed-intro-icon"></i>, а также отфильтровать по нужным направлениям{' '}
            <i className="fas fa-sliders-h news-feed-intro-icon"></i>.
          </p>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const fetchNewsItems = async () => {
      try {
        setLoading(true);
        setError(null);
        const items = await getNewsFeedItems();
        setNewsItems(items);
      } catch (err) {
        console.error('Error fetching news feed items:', err);
        setError('Ошибка загрузки ленты новостей');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsItems();
  }, []);

  if (loading) {
    return (
      <div className="news-feed-section">
        <div className="news-feed-section-header">
          <i className="fas fa-chart-line"></i>
        </div>
        <div className="news-feed-loading">
          <div className="loading-spinner"></div>
          <p>Загрузка дашбордов...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-feed-section">
        <div className="news-feed-section-header">
          <i className="fas fa-chart-line"></i>
        </div>
        <div className="news-feed-error">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (newsItems.length === 0) {
    return (
      <div className="news-feed-section">
        <div className="news-feed-section-header">
          <i className="fas fa-chart-line"></i>
        </div>
        <div className="news-feed-tools-row">
          <div className="news-feed-filter-wrap" ref={filterRef}>
            <button
              type="button"
              className={`news-feed-filter-btn ${filterOpen ? 'active' : ''} ${selectedTags.size > 0 ? 'has-filters' : ''}`}
              onClick={() => setFilterOpen((v) => !v)}
              title="Фильтр по тегам"
              aria-expanded={filterOpen}
            >
              <i className="fas fa-sliders-h" aria-hidden="true"></i>
              {selectedTags.size > 0 && (
                <span className="news-feed-filter-count">{selectedTags.size}</span>
              )}
            </button>
            {filterOpen && (
              <div className="news-feed-filter-dropdown">
                <div className="news-feed-filter-dropdown-title">Направления</div>
                {allTags.length === 0 ? (
                  <p className="news-feed-filter-empty">Нет тегов</p>
                ) : (
                  <ul className="news-feed-filter-list">
                    {allTags.map((tag) => (
                      <li key={tag}>
                        <label className="news-feed-filter-label">
                          <input
                            type="checkbox"
                            checked={selectedTags.has(tag)}
                            onChange={() => toggleTag(tag)}
                            className="news-feed-filter-checkbox"
                          />
                          <span>{tag}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {selectedTags.size > 0 && (
                  <button
                    type="button"
                    className="news-feed-filter-reset"
                    onClick={() => setSelectedTags(new Set())}
                  >
                    Сбросить
                  </button>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className={`news-feed-info-btn ${introOpen ? 'active' : ''}`}
            title="О проекте дашбордов"
            aria-expanded={introOpen}
            onClick={() => setIntroOpen((v) => !v)}
          >
            <i className="fas fa-lightbulb" aria-hidden="true"></i>
          </button>
        </div>
        {introOpen && introContent}
        <div className="news-feed-empty">
          <i className="fas fa-chart-line"></i>
          <p>Пока нет доступных дашбордов</p>
          <small>Новые аналитические дашборды появятся здесь</small>
        </div>
      </div>
    );
  }

  return (
    <div className="news-feed-section">
      <div className="news-feed-section-header">
        <i className="fas fa-chart-line"></i>
      </div>
      <div className="news-feed-tools-row">
        <div className="news-feed-filter-wrap" ref={filterRef}>
          <button
            type="button"
            className={`news-feed-filter-btn ${filterOpen ? 'active' : ''} ${selectedTags.size > 0 ? 'has-filters' : ''}`}
            onClick={() => setFilterOpen((v) => !v)}
            title="Фильтр по тегам"
            aria-expanded={filterOpen}
          >
            <i className="fas fa-sliders-h" aria-hidden="true"></i>
            {selectedTags.size > 0 && (
              <span className="news-feed-filter-count">{selectedTags.size}</span>
            )}
          </button>
          {filterOpen && (
            <div className="news-feed-filter-dropdown">
              <div className="news-feed-filter-dropdown-title">Направления</div>
              {allTags.length === 0 ? (
                <p className="news-feed-filter-empty">Нет тегов</p>
              ) : (
                <ul className="news-feed-filter-list">
                  {allTags.map((tag) => (
                    <li key={tag}>
                      <label className="news-feed-filter-label">
                        <input
                          type="checkbox"
                          checked={selectedTags.has(tag)}
                          onChange={() => toggleTag(tag)}
                          className="news-feed-filter-checkbox"
                        />
                        <span>{tag}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              {selectedTags.size > 0 && (
                <button
                  type="button"
                  className="news-feed-filter-reset"
                  onClick={() => setSelectedTags(new Set())}
                >
                  Сбросить
                </button>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className={`news-feed-info-btn ${introOpen ? 'active' : ''}`}
          title="О проекте дашбордов"
          aria-expanded={introOpen}
          onClick={() => setIntroOpen((v) => !v)}
        >
          <i className="fas fa-lightbulb" aria-hidden="true"></i>
        </button>
      </div>
      {introOpen && introContent}
      <div className="news-feed-container">
        {filteredItems.length === 0 ? (
          <div className="news-feed-no-results">
            <i className="fas fa-sliders-h" aria-hidden="true"></i>
            <p>Нет дашбордов по выбранным направлениям</p>
            <button type="button" className="news-feed-filter-clear-btn" onClick={() => setSelectedTags(new Set())}>
              Сбросить фильтр
            </button>
          </div>
        ) : (
          filteredItems.map((item) => (
            <NewsFeedItem key={item.id} item={item} />
          ))
        )}
      </div>
    </div>
  );
};

export default NewsFeedSection;
